import React, { useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown } from 'lucide-react';
// styles
import styles from './styles.module.css';
// utils
import useFlashMessage from '../../hooks/useFlashMessage';
import { generateInvoicePdf } from '../../utils/generateInvoicePdf';
import { Context } from '../../context/UserContext';

export default function Modal({ createdInvoice, setShowModal, setCreatedInvoice, user, setUser, children }) {
    const { updateInvoiceStatus, generateReceiptFromInvoice, revertReceiptToInvoice, setInvoices, registerPdf } = useContext(Context);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(createdInvoice?.status);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [receiptLoading, setReceiptLoading] = useState(false);
    const [isReceipt, setIsReceipt] = useState(!!createdInvoice?.isReceipt);
    const { setFlashMessage } = useFlashMessage();

    async function handleStatusChange(newStatus) {
        if (newStatus === currentStatus) {
            setStatusMenuOpen(false);
            return;
        }

        const token = JSON.parse(localStorage.getItem('token'));
        const res = await updateInvoiceStatus(token, createdInvoice._id, newStatus);

        if (!res?.error) {
            setCurrentStatus(newStatus);

            if (typeof setCreatedInvoice === 'function') {
                setCreatedInvoice(prev => ({ ...prev, status: newStatus }));
            }

            setInvoices(prev =>
                prev.map(inv =>
                    inv._id === createdInvoice._id ? { ...inv, status: newStatus } : inv
                )
            );
        }

        setStatusMenuOpen(false);
    }

    function applyReceiptChange(nextIsReceipt, nextStatus) {
        setIsReceipt(nextIsReceipt);
        setCurrentStatus(nextStatus);

        if (typeof setCreatedInvoice === 'function') {
            setCreatedInvoice(prev => ({ ...prev, isReceipt: nextIsReceipt, status: nextStatus }));
        }

        setInvoices(prev =>
            prev.map(inv =>
                inv._id === createdInvoice._id
                    ? { ...inv, isReceipt: nextIsReceipt, status: nextStatus }
                    : inv
            )
        );
    }

    async function handleGenerateReceipt() {
        setReceiptLoading(true);
        const token = JSON.parse(localStorage.getItem('token'));
        const res = await generateReceiptFromInvoice(token, createdInvoice._id);
        if (!res?.error && res?.invoice) applyReceiptChange(true, res.invoice.status);
        setReceiptLoading(false);
    }

    async function handleRevertReceipt() {
        setReceiptLoading(true);
        const token = JSON.parse(localStorage.getItem('token'));
        const res = await revertReceiptToInvoice(token, createdInvoice._id);
        if (!res?.error && res?.invoice) applyReceiptChange(false, res.invoice.status);
        setReceiptLoading(false);
    }

    // Trava o scroll do body enquanto o modal está aberto
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'auto'; };
    }, []);

    // Retorna o domingo (00:00) da semana da data informada
    const getStartOfWeek = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - d.getDay());
        return d;
    };

    const startOfCurrentWeek = getStartOfWeek(new Date());

    // Só conta os PDFs cuja data caia dentro da semana atual
    const pdfsThisWeekCount = (user.pdfsGeneratedThisWeek || []).filter(
        (p) => new Date(p.date) >= startOfCurrentWeek
    ).length;

    const pdfLimitReached =
        user.plan !== 'premium' &&
        pdfsThisWeekCount >= (user.plan === 'plus' ? 5 : 1);

    return createPortal(
        <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
            {createdInvoice ? (
                <div className={styles.modal}>

                    {/* ── Header ── */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <span className={styles.headerLabel}>{isReceipt ? 'Recibo' : 'Orçamento'}</span>
                            <h3 className={styles.headerNumber}>{createdInvoice.number}</h3>
                        </div>
                        <button className={styles.closeBtn} onClick={() => setShowModal(false)}>
                            <X size={20} />
                        </button>
                    </div>

                    {/* ── Conteúdo scrollável ── */}
                    <div className={styles.invoiceContainer}>
                        <div className={styles.content}>
                            {children}
                        </div>
                    </div>

                    {/* ── Footer com ações ── */}
                    <div className={styles.footer}>
                        {/* Status dropdown */}
                        <div className={styles.statusWrapper}>
                            <button
                                className={`${styles.statusBtn} ${styles[currentStatus]}`}
                                onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                            >
                                {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
                                <ChevronDown size={15} />
                            </button>

                            {statusMenuOpen && (
                                <div className={styles.statusDropdown}>
                                    {['pendente', 'enviada', 'confirmada', 'paga', 'cancelada'].map((status) => (
                                        <button
                                            key={status}
                                            className={`${styles.statusOption} ${styles[status]}`}
                                            onClick={() => handleStatusChange(status)}
                                        >
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Gerar recibo (só se ainda não for um) */}
                        <button
                            className={styles.receiptBtn}
                            onClick={isReceipt ? handleRevertReceipt : handleGenerateReceipt}
                            disabled={receiptLoading}
                        >
                            {receiptLoading
                                ? 'Aguarde...'
                                : isReceipt ? 'Voltar para orçamento' : 'Gerar Recibo'}
                        </button>
                        {/* Baixar PDF */}
                        <button
                            className={`${styles.pdfBtn} ${pdfLimitReached ? styles.pdfBtnDisabled : ''}`}
                            onClick={async () => {
                                setPdfLoading(true);
                                await generateInvoicePdf(
                                    { ...createdInvoice, isReceipt, status: currentStatus },
                                    user, setUser, setFlashMessage, registerPdf
                                );
                                setPdfLoading(false);
                            }}
                            disabled={pdfLimitReached || pdfLoading}
                        >
                            {pdfLimitReached ? 'Limite atingido' : pdfLoading ? 'Gerando PDF...' : 'Baixar PDF'}
                        </button>
                    </div>

                </div>
            ) : (
                <p className={styles.loading}>Carregando dados da nota...</p>
            )}
        </div>,
        document.body
    );
}